// ─── Automatic best-seller ranking ──────────────────────────────────────
//
// Pure logic that turns a "units sold per product" tally into the set of
// product ids that should be tagged as Best Sellers. Keeping this pure (no
// React, no Firebase) makes the ranking rule easy to reason about and reuse.
//
// The rule: a product must have sold at least `minUnits` to qualify, then the
// top `topN` qualifying products (by units sold) are tagged. On a brand-new
// site with no sales, this returns an empty set — so nothing is tagged until
// real orders come in and one product genuinely outsells the rest.

export interface BestSellerOptions {
  /** How many top products to tag as best sellers. */
  topN?: number
  /** Minimum units sold before a product can qualify at all. */
  minUnits?: number
}

export function computeBestSellers(
  counts: Record<string, number>,
  options: BestSellerOptions = {},
): Set<string> {
  const { topN = 5, minUnits = 1 } = options

  const ranked = Object.entries(counts)
    .filter(([, units]) => units >= minUnits)
    // Highest sellers first; tie-break alphabetically for a stable order.
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, topN)
    .map(([productId]) => productId)

  return new Set(ranked)
}
